import React, { useState, useCallback, useMemo, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { SnackbarContext } from './SnackbarContext';
import { SnackbarOptions, Snackbar, SnackbarContextType } from './types';

interface SnackbarProviderProps {
  children: ReactNode;
}

const DEFAULT_SNACKBAR_OPTIONS: SnackbarOptions = {
  autoHideDuration: 5000,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  }
};

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children
}) => {
  const [snackbar, setSnackbar] = useState<Snackbar | null>(null);

  const showSnackbar = useCallback(
    (message: string, options: SnackbarOptions = {}): void => {
      const newSnackbar: Snackbar = {
        id: uuidv4(),
        message,
        options: {
          ...DEFAULT_SNACKBAR_OPTIONS,
          ...options
        }
      };

      setSnackbar(newSnackbar);
    },
    []
  );

  const dismissSnackbar = useCallback((): void => {
    setSnackbar(null);
  }, []);

  const contextValue = useMemo<SnackbarContextType>(
    () => ({
      snackbar,
      showSnackbar,
      dismissSnackbar
    }),
    [snackbar, showSnackbar, dismissSnackbar]
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
    </SnackbarContext.Provider>
  );
};

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default SnackbarProvider;
